import type { CoastalSceneState } from './coastal-types';

export type WindowMoment = { id: 'crab' | 'gull' | 'glove'; label: string; start: number };
export type WindowMomentWeather = { daylight: boolean; windKmh: number; rainMm: number };
export const WINDOW_MOMENT_SECONDS = { gull: 12, glove: 10 } as const;

function seedHash(seed: string): number {
  let value = 2166136261;
  for (const character of seed.slice(0, 512)) value = Math.imul(value ^ character.charCodeAt(0), 16777619) >>> 0;
  return value;
}

/** Small deterministic antics, never predicted wildlife, accidents or hazards. */
export function windowMomentAt(kind: 'coast' | 'mountain', seed: string, elapsed: number, weather: WindowMomentWeather): WindowMoment | null {
  if (!seed || !Number.isFinite(elapsed) || elapsed < 0 || !weather.daylight
    || !Number.isFinite(weather.windKmh) || weather.windKmh < 0
    || !Number.isFinite(weather.rainMm) || weather.rainMm < 0) return null;
  const mountain = kind === 'mountain';
  if (mountain ? weather.windKmh < 6 || weather.windKmh > 32 || weather.rainMm > 1
    : weather.windKmh > 22 || weather.rainMm > .2) return null;
  const hash = seedHash(seed);
  const period = mountain ? 108 : 124;
  const delay = mountain ? 18 + hash % 28 : 16 + hash % 36;
  const start = Math.floor(elapsed / period) * period + delay;
  const id = mountain ? 'glove' : 'gull';
  if (elapsed < start || elapsed >= start + WINDOW_MOMENT_SECONDS[id]) return null;
  return { id, start, label: mountain ? 'A runaway glove tumbles down the slope.' : 'A gull makes off with a snack.' };
}

/** At most two coastal cameos can overlap. The crab is drawn by the sea scene. */
export function coastalWindowMomentsAt(state: CoastalSceneState, elapsed: number): WindowMoment[] {
  const crab = surfCrabCycleAt(state, elapsed);
  const gull = windowMomentAt('coast', state.seed, elapsed, { daylight: state.weather.daylight,
    windKmh: state.wind.speedKmh, rainMm: state.weather.rainMm });
  const moments: WindowMoment[] = crab ? [{ id: 'crab', start: crab.start, label: 'A crab scuttles along the beach.' }] : [];
  if (gull) moments.push(gull);
  return moments;
}

/** Shared timing for the existing crab drawing and its replay link. */
export function surfCrabCycleAt(state: Pick<CoastalSceneState, 'seed' | 'weather' | 'wind'>, elapsed: number) {
  if (!Number.isFinite(elapsed) || elapsed < 0 || !state.weather.daylight
    || !Number.isFinite(state.weather.rainMm) || state.weather.rainMm < 0 || state.weather.rainMm > 2
    || !Number.isFinite(state.wind.speedKmh) || state.wind.speedKmh < 0 || state.wind.speedKmh > 35) return null;
  let hash = 0;
  for (let i = 0; i < state.seed.length; i++) hash = (Math.imul(hash, 31) + state.seed.charCodeAt(i)) >>> 0;
  const offset = hash % 29, time = elapsed + offset;
  const cycle = Math.floor(time / 80), phase = (time % 80 - 18) / 12;
  if (phase <= 0 || phase >= 1) return null;
  // The first occurrence may already be in progress at timeline zero.
  return { cycle, phase, start: Math.max(0, cycle * 80 + 18 - offset) };
}
